import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { FaAward, FaChevronLeft, FaChevronRight } from "react-icons/fa";

/* ─────────────────────────────────────────
   IMAGE SLOTS — add require() paths when ready
───────────────────────────────────────── */
const categoryCarousels = [
  [
    require("../assets/images/summit/hyundai.webp"),
    require("../assets/images/summit/irfc.webp"),
    require("../assets/images/summit/PowerFinanceCorporationLtd.webp"),
    require("../assets/images/summit/Premji.webp"),
  ], // Visionary Partnership Award
  [
    require("../assets/images/summit/mphasis.webp"),
    require("../assets/images/summit/hindalco.webp"),
    require("../assets/images/summit/hsbc.webp"),
    require("../assets/images/summit/herbalife.webp"),
    require("../assets/images/summit/cams.webp"),
  ], // Tech Pioneer Award
  [
    require("../assets/images/summit/amex.webp"),
    require("../assets/images/summit/hdfc.webp"),
    require("../assets/images/summit/ntpc.webp"),
    require("../assets/images/summit/fedex.webp"),
    require("../assets/images/summit/coalindia.webp"),
  ], // Trailblazer in Academic Alliance Award
  [
    require("../assets/images/summit/hyundai82.webp"),
    require("../assets/images/summit/idbi.webp"),
    require("../assets/images/summit/powergrid.webp"),
  ], // Excellence in Collaborative Innovation
];

/* ─────────────────────────────────────────
   CAROUSEL COMPONENT
───────────────────────────────────────── */
const Carousel = ({ images }) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);
  const total = images.length;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  useEffect(() => {
    if (total < 2) return;
    timerRef.current = setInterval(() => setCurrent((c) => (c + 1) % total), 3500);
    return () => clearInterval(timerRef.current);
  }, [total]);

  return (
    <CarouselWrap>
      <CarouselTrack>
        {total > 0 ? (
          <CarouselImg src={images[current]} alt={`Slide ${current + 1}`} />
        ) : (
          <CarouselPlaceholder>
            <PlaceholderText>Images coming soon</PlaceholderText>
          </CarouselPlaceholder>
        )}
      </CarouselTrack>
      <CarouselControls>
        <CtrlBtn onClick={prev}><FaChevronLeft /></CtrlBtn>
        <Dots>
          {total > 0
            ? images.map((_, i) => <Dot key={i} active={i === current} onClick={() => setCurrent(i)} />)
            : Array.from({ length: 5 }).map((_, i) => <Dot key={i} active={i === 0} />)
          }
        </Dots>
        <CtrlBtn onClick={next}><FaChevronRight /></CtrlBtn>
      </CarouselControls>
    </CarouselWrap>
  );
};

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const categories = [
  {
    title: "Visionary Partnership Award",
    recipients: [
      { org: "Hyundai Motor India Ltd", project: "Hydrogen Valley Innovation Hub project" },
      { org: "Premji Invest", project: "Sudha Gopalakrishnan Brain Centre project" },
      { org: "Power Finance Corporation Ltd", project: "Establishing the Anatomy Lab at IIT Madras" },
      { org: "Indian Railway Finance Corporation", project: "Establishment of the SCOT Research Lab" },
    ],
  },
  {
    title: "Tech Pioneer Award",
    recipients: [
      { org: "Mphasis Limited", project: "Mphasis Centre for Quantum Information, Communication and Computing (MCQuICC)" },
      { org: "Hindalco Industries Limited", project: "Sudha Gopalakrishnan Brain Centre project" },
      { org: "The Hongkong and Shanghai Banking Corporation Limited, India", project: "Centre for Resource Efficiency, Recyclability & Circularity" },
      { org: "Herbalife International India Pvt. Ltd.", project: "Establishing the Herbalife IITM Plant Cell Fermentation Technology Lab" },
      { org: "Computer Age Management Services", project: "CAMS IITM Fintech Innovation Lab" },
    ],
  },
  {
    title: "Trailblazer in Academic Alliance Award",
    recipients: [
      { org: "American Express India Private Limited", project: "Innovation & Incubation Program at IIT Madras" },
      { org: "HDFC Bank Limited", project: "HDFC Parivartan Startup Grants Program" },
      { org: "NTPC Limited", project: "NTPC Centre of Excellence in Cardiovascular Research" },
      { org: "FedEx Corporate Services", project: "FedEx Center for Smart and Sustainable Supply Chains" },
      { org: "Coal India Limited", project: "Centre for Sustainable Energy" },
    ],
  },
  {
    title: "Excellence in Collaborative Innovation",
    recipients: [
      { org: "Hyundai Motor India Foundation", project: "Hyundai Center for Cancer Genomics – Pediatric Cancer" },
      { org: "Kotak Mahindra Bank", project: "Kotak IIT Madras Save Energy Mission (KISEM) program" },
      { org: "IDBI Bank Limited", project: "IDBI Cybersecurity Lab" },
      { org: "Power Grid Corporation of India Limited", project: "PowerGrid Vidyalakshmi PAN IIT Merit Cum Means & Endowment Scholarships" },
    ],
  },
];

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const PartnershipAwards = () => {
  return (
    <Section id="partnership-awards">
      <SectionLabel>Summit Highlights</SectionLabel>
      <MainHeading>IITM Partnership Awards 2026</MainHeading>
      <Divider />
      <Description>
        The IITM Partnership Awards recognize organizations driving impactful and forward-looking
        collaborations with IIT Madras in research, innovation, technology development, and
        entrepreneurship, with a strong focus on sustainability, scalability, and societal impact.
      </Description>
      

      <Wrapper>
        {categories.map((cat, i) => {
          const imageRight = i % 2 === 0;
          const carouselCol = (
            <CarouselCol>
              <Carousel images={categoryCarousels[i]} />
            </CarouselCol>
          );
          const infoCol = (
            <InfoCol>
              <CatTitleRow>
                <FaAward />
                <CatTitle>{cat.title}</CatTitle>
              </CatTitleRow>
              <RecipientList>
                {cat.recipients.map((r, j) => (
                  <RecipientItem key={j}>
                    <OrgName>{r.org}</OrgName>
                    <ProjectName>{r.project}</ProjectName>
                  </RecipientItem>
                ))}
              </RecipientList>
            </InfoCol>
          );
          return (
            <CategoryBlock key={i}>
              {imageRight ? <>{infoCol}{carouselCol}</> : <>{carouselCol}{infoCol}</>}
            </CategoryBlock>
          );
        })}
      </Wrapper>
    </Section>
  );
};

export default PartnershipAwards;


/* ═══════════════════════════════════════
   STYLES
═══════════════════════════════════════ */

const Section = styled.section`
  padding: 20px 20px;
  background: #faf8f5;

  @media (max-width: 768px) {
    padding: 36px 14px;
  }
`;

const SectionLabel = styled.p`
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #d6a54f;
  margin-bottom: 10px;
`;

const MainHeading = styled.h2`
  text-align: center;
  font-size: 38px;
  font-weight: 700;
  color: #791e1b;
  margin: 0 0 16px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Description = styled.p`
  text-align: center;
  max-width: 820px;
  margin: 0 auto 20px;
  font-size: 15px;
  line-height: 1.8;
  color: #4b5563;
`;

const Divider = styled.div`
  width: 60px;
  height: 4px;
  background: #d6a54f;
  border-radius: 2px;
  margin: 0 auto 22px;
`;

const Wrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const CategoryBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.08);
  border: 1px solid #ede8e0;
  min-height: 320px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCol = styled.div`
  background: #ffffff;
  padding: 36px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const CatTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #791e1b;
  font-size: 20px;
`;

const CatTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #791e1b;
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

const RecipientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RecipientItem = styled.div`
  border-left: 3px solid #d6a54f;
  padding-left: 14px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const OrgName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #171f32;
`;

const ProjectName = styled.span`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const CarouselCol = styled.div`
  background: #171f32;
  display: flex;
  flex-direction: column;
`;

const CarouselWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CarouselTrack = styled.div`
  flex: 1;
  overflow: hidden;
`;

const CarouselImg = styled.img`
  width: 100%;
  height: 100%;
  min-height: 320px;
  object-fit: cover;
  display: block;
`;

const CarouselPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  background: repeating-linear-gradient(
    135deg,
    #1e2740 0px,
    #1e2740 18px,
    #171f32 18px,
    #171f32 36px
  );
`;

const PlaceholderText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #d6a54f;
  letter-spacing: 1px;
  background: rgba(0, 0, 0, 0.35);
  padding: 6px 16px;
  border-radius: 20px;
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background: #171f32;
`;

const CtrlBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.button`
  width: ${(p) => (p.active ? "20px" : "8px")};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${(p) => (p.active ? "#ffffff" : "rgba(255,255,255,0.3)")};
  cursor: pointer;
  transition: width 0.3s ease, background 0.3s ease;
  padding: 0;
`;
